// src/auth/Signup.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, Link as RouterLink, useLocation } from "react-router-dom";
import { API } from "../config/apiConfig";
import { portalGradientTextSx } from "../styles/dashboardBrand";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Link,
} from "@mui/material";

function Signup() {
  const location = useLocation();

  const getRefCodeFromURL = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("ref") || "";
  };

  const initialRefCode = getRefCodeFromURL();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    confirmEmail: "",
    password: "",
    confirmPassword: "",
    referralCode: initialRefCode,
  });
  const [referrer, setReferrer] = useState(null);
  const [referrerName, setReferrerName] = useState("");
  const [checkingReferrer, setCheckingReferrer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  /** Validates referral code; returns true if the API recognizes a referrer. */
  const validateReferralCode = async (codeToValidate = null) => {
    const code = String(codeToValidate ?? form.referralCode).trim();
    if (!code) {
      setReferrer(null);
      setReferrerName("");
      return false;
    }

    try {
      setCheckingReferrer(true);
      setReferrer(null);
      setReferrerName("");

      const res = await axios.get(API.validateReferral(code));

      if (res.data.success && res.data.referrer) {
        const ref = res.data.referrer;
        setReferrer(ref);
        const displayName =
          [ref.firstName, ref.lastName].filter(Boolean).join(" ") ||
          ref.userName ||
          ref.referralCode;

        setReferrerName(`Referred by: ${displayName}`);
        return true;
      }
      setReferrerName("Invalid referral code");
      setReferrer(null);
      return false;
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Could not verify referral code";
      setReferrerName(errorMsg);
      setReferrer(null);
      return false;
    } finally {
      setCheckingReferrer(false);
    }
  };

  useEffect(() => {
    const refCode = getRefCodeFromURL();
    if (refCode) {
      setForm((prev) => ({ ...prev, referralCode: refCode }));
      const t = setTimeout(() => validateReferralCode(refCode), 100);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "referralCode") {
      setForm((prev) => ({ ...prev, referralCode: value }));
      setReferrer(null);
      setReferrerName("");
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (form.email !== form.confirmEmail) {
      setError("Emails do not match");
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    const refTrim = String(form.referralCode).trim();
    if (!refTrim) {
      setError("Referral code is required.");
      return;
    }

    const referralOk = await validateReferralCode(refTrim);
    if (!referralOk) {
      setError("This referral code is not valid.");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        password: form.password,
        confirmPassword: form.confirmPassword,
        referredByCode: refTrim,
      };

      const res = await axios.post(API.signup, payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (res.data.success) {
        navigate("/verify-email", { state: { userId: res.data.userId } });
      } else {
        setError(res.data.error || "Signup failed");
      }
    } catch (err) {
      console.error("❌ Signup error:", err.response?.data || err.message);
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "❌ Server error while signing up"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack
      sx={{
        minHeight: "100vh",
        alignItems: "center",
        justifyContent: "center",
        background: 'linear-gradient(135deg, #4FACFE 0%, #6366F1 40%, #9333EA 72%, #E11D48 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.2) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Paper 
        elevation={24} 
        sx={{ 
          p: 5, 
          width: "100%", 
          maxWidth: 450,
          borderRadius: 3,
          bgcolor: 'background.paper',
          backdropFilter: 'blur(20px)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          textAlign="center"
          sx={portalGradientTextSx({
            fontWeight: 700,
            mb: 2,
          })}
        >
          Create Account
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              name="firstName"
              label="First Name"
              value={form.firstName}
              onChange={handleChange}
              required
              fullWidth
              disabled={loading}
            />
            <TextField
              name="lastName"
              label="Last Name"
              value={form.lastName}
              onChange={handleChange}
              required
              fullWidth
              disabled={loading}
            />
            <TextField
              name="email"
              label="Email"
              type="email"
              value={form.email}
              onChange={handleChange}
              required
              fullWidth
              disabled={loading}
            />
            <TextField
              name="confirmEmail"
              label="Verify Email"
              type="email"
              value={form.confirmEmail}
              onChange={handleChange}
              required
              fullWidth
              disabled={loading}
              error={form.confirmEmail.length > 0 && form.email !== form.confirmEmail}
              helperText={
                form.confirmEmail.length > 0 && form.email !== form.confirmEmail
                  ? "Emails do not match"
                  : ""
              }
            />
            <TextField
              name="password"
              label="Password"
              type="password"
              value={form.password}
              onChange={handleChange}
              required
              fullWidth
              disabled={loading}
            />
            <TextField
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              value={form.confirmPassword}
              onChange={handleChange}
              required
              fullWidth
              disabled={loading}
            />
            <TextField
              name="referralCode"
              label="Referral Code"
              value={form.referralCode}
              onChange={handleChange}
              onBlur={() => validateReferralCode()}
              required
              fullWidth
              disabled={loading}
              helperText={
                checkingReferrer
                  ? "Checking referrer..."
                  : "Enter the referral code of the member who invited you."
              }
            />
            {Boolean(form.referralCode.trim()) && referrerName && !checkingReferrer && (
              <div style={{ marginTop: '8px', fontSize: '14px' }}>
                <span
                  style={{
                    color: referrer ? '#16a34a' : '#dc2626',
                    fontWeight: referrer ? '600' : '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {referrer ? '✅' : '❌'} {referrerName}
                </span>
              </div>
            )}

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading || checkingReferrer}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Sign Up"
              )}
            </Button>

            <Typography variant="body2" textAlign="center">
              Already have an account?{" "}
              <Link component={RouterLink} to="/login" underline="hover">
                Login here
              </Link>
            </Typography>
          </Stack>
        </form>
      </Paper>
    </Stack>
  );
}

export default Signup;
